import * as React from "react";
import {
  Box,
  Chip,
  CircularProgress,
  Paper,
  Pagination,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CustomSelect from "../../../../../components/CustomSelect";
import SearchInput from "../../../../../components/SearchInput";
import CustomModal from "../../../../../components/CustomModal";
import CustomButton from "../../../../../components/CustomButton";
import CustomInput from "../../../../../components/CustomInput";
import ScreenLayout from "../../../../../components/ScreenLayout";
import PageHeader from "../../../../../components/PageHeader";
import DownloadIcon from "@mui/icons-material/Download";
import FilterListIcon from "@mui/icons-material/FilterList";
import PhoneIcon from "@mui/icons-material/Phone";
import Button from "@mui/material/Button";
import moment from "moment";
import axiosClient from "../../../../../utils/api/axiosClient";
import { getToken } from "../../../../../utils/functions/index.js";
import ViewModal from "../premium-member/modals/ViewModal";
import IconButton from "../../../../../components/icon-button";
import { MemberData } from "../types";

type StateOption = {
  id: number;
  name: string;
};

const MEMBER_TYPES = [
  { key: "free",    label: "Free",    color: "#212529", bg: "#e3f2fd", rowBg: "#e3f2fd" },
  { key: "premium", label: "Premium", color: "#ffffff", bg: "#10b981", rowBg: "#d1fae5" },
  { key: "expired", label: "Expired", color: "#ffffff", bg: "#f59e0b", rowBg: "#fef3c7" },
  { key: "deleted", label: "Deleted", color: "#ffffff", bg: "#ef4444", rowBg: "#fee2e2" },
];

function getMemberType(row: MemberData): string {
  // Use memberType from backend if available, otherwise compute as fallback
  if (row.memberType) return row.memberType;
  if (row.deleted_at) return "deleted";
  if (row.status === "ACTIVE") return "premium";
  if (row.status === "EXPIRED") return "expired";
  return "free";
}

function hasProfileImage(row: MemberData): boolean {
  if (typeof row?.hasProfileImage === "boolean") return row.hasProfileImage;
  if (typeof (row as any)?.hasProfileImage === "string") {
    const value = String((row as any).hasProfileImage).toLowerCase();
    if (value === "true") return true;
    if (value === "false") return false;
  }
  if (row?.profile?.img || row?.profile?.profileImage || row?.profile?.profilePicture) return true;
  if (Array.isArray(row?.files)) {
    return row.files.some((file: any) => {
      const galleryType = String(file?.galleryType || file?.type || "").toUpperCase();
      const fileName = String(file?.fileName || "").toLowerCase();
      return galleryType === "PROFILE_IMAGE" || fileName.includes("profile");
    });
  }
  return false;
}

const PAGE_SIZE = 50;

const HEADERS = [
  { label: "S.No", field: "id" },
  { label: "Type", field: "memberType" },
  { label: "ID", field: "id" },
  { label: "First Name", field: "firstName" },
  { label: "Last Name", field: "lastName" },
  { label: "Gender", field: "gender" },
  { label: "Contact", field: "username" },
  { label: "Alt Contact 1", field: "alt1Contact" },
  { label: "Alt Contact 2", field: "alt2Contact" },
  { label: "Member ID", field: "memberId" },
  { label: "Native State", field: "nativeState" },
  { label: "Current State", field: "currentState" },
  { label: "Has Profile Image", field: "hasProfileImage" },
  { label: "Date of Birth", field: "dateOfBirth" },
  { label: "Registered On", field: "created_at" },
  { label: "Platform", field: "platform" },
  { label: "Active", field: "isActive" },
  { label: "Hard Delete", field: "hardDelete" },
  { label: "Actions", field: "actions" }
];

const cellSx = { py: "4px", px: "8px", fontSize: "0.75rem", whiteSpace: "nowrap", borderBottom: "1px solid #e5e7eb" };
const headSx = { ...cellSx, fontWeight: 700, bgcolor: "#212529", color: "#ffffff", py: "8px", fontSize: "0.8rem" };

function DataField({ label, value }: { label: string; value: string | number | boolean | null | undefined }) {
  return (
    <Box>
      <Typography variant="caption" sx={{ color: "#6b7280", fontSize: "0.75rem", display: "block" }}>{label}</Typography>
      <Typography variant="body2" sx={{ color: "#212529", fontSize: "0.875rem", fontWeight: 500 }}>{value ?? "—"}</Typography>
    </Box>
  );
}

export default function AllMembers() {
  const [rows, setRows] = React.useState<MemberData[]>([]);
  const [states, setStates] = React.useState<StateOption[]>([]);
  const [profileImageStatusByUserId, setProfileImageStatusByUserId] = React.useState<Record<number, boolean>>({});
  const [totalRows, setTotalRows] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(PAGE_SIZE);
  const [isLoading, setIsLoading] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [modalShow, setModalShow] = React.useState(false);
  const [selectedUser, setSelectedUser] = React.useState<MemberData | null>(null);
  const [filterModalOpen, setFilterModalOpen] = React.useState(false);
  const [filterField, setFilterField] = React.useState<string>("");
  const [filterValue, setFilterValue] = React.useState<string>("");
  const [filterOperator, setFilterOperator] = React.useState<string>("contains");
  const [filterType, setFilterType] = React.useState<string>("");
  const [callModalOpen, setCallModalOpen] = React.useState(false);
  const [callNumber, setCallNumber] = React.useState<string>("");
  const [allDataModalOpen, setAllDataModalOpen] = React.useState(false);
  const [selectedRowData, setSelectedRowData] = React.useState<MemberData | null>(null);
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [confirmAction, setConfirmAction] = React.useState<{ type: string; userId: number; value?: boolean } | null>(null);
  const [sortField, setSortField] = React.useState<string>("id");
  const [sortDirection, setSortDirection] = React.useState<"asc" | "desc">("desc");
  const [isExporting, setIsExporting] = React.useState(false);
  const isFetchingRef = React.useRef(false);
  const pageSizeRef = React.useRef(pageSize);
  const filterFieldRef = React.useRef(filterField);
  const filterValueRef = React.useRef(filterValue);
  const filterOperatorRef = React.useRef(filterOperator);
  const filterTypeRef = React.useRef(filterType);
  const searchRef = React.useRef(search);
  const sortFieldRef = React.useRef(sortField);
  const sortDirectionRef = React.useRef(sortDirection);

  React.useEffect(() => { pageSizeRef.current = pageSize; }, [pageSize]);
  React.useEffect(() => { filterFieldRef.current = filterField; }, [filterField]);
  React.useEffect(() => { filterValueRef.current = filterValue; }, [filterValue]);
  React.useEffect(() => { filterOperatorRef.current = filterOperator; }, [filterOperator]);
  React.useEffect(() => { filterTypeRef.current = filterType; }, [filterType]);
  React.useEffect(() => { searchRef.current = search; }, [search]);
  React.useEffect(() => { sortFieldRef.current = sortField; }, [sortField]);
  React.useEffect(() => { sortDirectionRef.current = sortDirection; }, [sortDirection]);

  React.useEffect(() => {
    const headers = { Authorization: getToken() };
    axiosClient
      .get("v1/states", { headers, skipGlobalLoader: true })
      .then((res) => {
        setStates(res.data || []);
      })
      .catch(() => {
        setStates([]);
      });
  }, []);

  const stateNameById = React.useMemo(() => {
    const map = new Map<number, string>();
    states.forEach((item) => {
      if (item?.id != null && item?.name) map.set(Number(item.id), item.name);
    });
    return map;
  }, [states]);

  const getStateDisplay = React.useCallback((value: any) => {
    if (value === null || value === undefined || value === "") return "—";
    const numericValue = Number(value);
    if (!Number.isNaN(numericValue) && stateNameById.has(numericValue)) {
      return stateNameById.get(numericValue) || "—";
    }
    return String(value);
  }, [stateNameById]);

  React.useEffect(() => {
    if (!rows.length) return;
    const headers = { Authorization: getToken() };
    const pendingRows = rows.filter((row) => {
      if (!row?.id) return false;
      if (profileImageStatusByUserId[row.id] !== undefined) return false;
      return !hasProfileImage(row);
    });
    if (!pendingRows.length) return;

    Promise.allSettled(
      pendingRows.map((row) =>
        axiosClient
          .get(`/v1/galleries/profile-image/${row.id}`, { headers, skipGlobalLoader: true })
          .then((res) => ({ userId: row.id, hasImage: Boolean(res?.data?.content || res?.data?.fileName || res?.data?.filePath) }))
          .catch(() => ({ userId: row.id, hasImage: false }))
      )
    ).then((results) => {
      const nextMap: Record<number, boolean> = {};
      results.forEach((result) => {
        if (result.status === "fulfilled" && result.value?.userId) {
          nextMap[result.value.userId] = result.value.hasImage;
        }
      });
      if (Object.keys(nextMap).length) {
        setProfileImageStatusByUserId((prev) => ({ ...prev, ...nextMap }));
      }
    });
  }, [rows, profileImageStatusByUserId]);

  const getHasProfileImage = React.useCallback((row: MemberData) => {
    if (hasProfileImage(row)) return true;
    if (!row?.id) return false;
    return Boolean(profileImageStatusByUserId[row.id]);
  }, [profileImageStatusByUserId]);

  const fetchPage = React.useCallback((page: number, size?: number) => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);
    const headers = { Authorization: getToken() };
    const currentSize = size || pageSizeRef.current;

    const endpoint = `/v1/users/getAllMembers/${page}/${currentSize}`;
    const body: any = {};
    
    // Add type filter if selected
    if (filterTypeRef.current) {
      body.field = "memberType";
      body.value = filterTypeRef.current;
      body.operator = "equals";
    }
    // Add other field filter if selected
    else if (filterFieldRef.current && filterValueRef.current) {
      body.field = filterFieldRef.current;
      body.value = filterValueRef.current;
      body.operator = filterOperatorRef.current;
    }
    
    axiosClient
      .post(endpoint, body, {
        headers,
        skipGlobalLoader: true,
        params: {
          search: searchRef.current || "",
          sortField: sortFieldRef.current || "id",
          sortDirection: sortDirectionRef.current || "desc",
        },
      })
      .then((res) => {
        setTotalPages(res.data.totalPages ?? 1);
        setTotalRows(res.data.totalElements ?? 0);
        const newRows = (res.data.content || []).map((row: MemberData, idx: number) => ({
          ...row,
          _uid: `all-${row.id ?? idx}`,
          memberType: getMemberType(row),
        }));
        setRows(newRows);
        setCurrentPage(page);
      })
      .catch(console.log)
      .finally(() => { isFetchingRef.current = false; setIsLoading(false); });
  }, []);

  React.useEffect(() => { fetchPage(1); }, [fetchPage]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [search, fetchPage]);

  React.useEffect(() => {
    setCurrentPage(1);
    fetchPage(1);
  }, [sortField, sortDirection, fetchPage]);

  const activateUser = (userId: number, checked: boolean) => {
    setConfirmAction({ type: "activate", userId, value: checked });
    setConfirmModalOpen(true);
  };

  const blockUser = (userId: number) => {
    setConfirmAction({ type: "block", userId });
    setConfirmModalOpen(true);
  };

  const hardDeleteUser = (userId: number) => {
    setConfirmAction({ type: "hardDelete", userId });
    setConfirmModalOpen(true);
  };

  const handleConfirmAction = () => {
    if (!confirmAction) return;
    const headers = { Authorization: getToken() };
    if (confirmAction.type === "activate" && confirmAction.value !== undefined) {
      axiosClient.post(`v1/users/update-is-verified/${confirmAction.userId}/${confirmAction.value}`, {}, { headers })
        .then(() => fetchPage(currentPage)).catch(console.log);
    } else if (confirmAction.type === "block") {
      axiosClient.post(`v1/users/block-unblock/${confirmAction.userId}/true`, {}, { headers })
        .then(() => fetchPage(currentPage)).catch(console.log);
    } else if (confirmAction.type === "hardDelete") {
      axiosClient.post(`v1/users/hard-delete/${confirmAction.userId}`, {}, { headers })
        .then(() => fetchPage(currentPage)).catch(console.log);
    }
    setConfirmModalOpen(false);
    setConfirmAction(null);
  };

  const exportCSV = async () => {
    setIsExporting(true);
    const authHeaders = { Authorization: getToken() };
    let allData: MemberData[] = [];
    try {
      const res = await axiosClient.post(`/v1/users/exportAllMembers`, {}, { headers: authHeaders });
      allData = (res.data || []).map((row: any) => ({ ...row, memberType: getMemberType(row) }));
    } catch (e) {
      console.log(e);
      setIsExporting(false);
      return;
    }
    const csvHeaders = [
      "Type","ID","Username","First Name","Last Name","Gender","Date of Birth","Email","Mobile","Country Code",
      "Status","Active","Email Verified","Mobile Verified","On Behalf","Android App","iOS App","Web","Roles",
      "Member ID","Alt Contact 1","Alt Contact 2","Profile Verified",
      "Age","About You","Birth Order","Birth Place","Birth Time","Body Type","Caste","Economic Status",
      "Family Type","Father Name","Father Occupation","Marital Status","Mother Name","Mother Occupation",
      "Mother Tongue","No of Brothers","No of Brothers Married","No of Children","No of Sisters",
      "No of Sisters Married","Lagnam","Paadham","Profession","Qualification","Raasi","Raghu Kedhu",
      "Religion","Salary Details","Sevvai","Star","Sub Caste","Sub Caste Additional","Height","Weight",
      "Physical Status","Native Country","Native State","Native District","Native Address","Country",
      "District","State","Address","Property",
      "Created At","Updated At","Deleted At","Created By","Updated By","Deleted By","OTP"
    ];
    const csvRows = [
      csvHeaders.join(","),
      ...allData.map((r) => [
        r.memberType, r.id, r.username ?? "", r.firstName ?? "", r.lastName ?? "", r.gender ?? "",
        r.dateOfBirth ? moment(r.dateOfBirth).format("DD/MM/YYYY") : "",
        r.email ?? "", r.mobile ?? "", r.countryCode ?? "", r.status ?? "",
        r.isActive ? "Yes" : "No", r.isEmailVerified ? "Yes" : "No", r.isMobileVerified ? "Yes" : "No",
        r.onBehalf?.name ?? "", r.hasAndroidApp ? "Yes" : "No", r.hasIosApp ? "Yes" : "No", r.hasWeb ? "Yes" : "No",
        r.roles?.map((role: { name: string }) => role.name).join("; ") ?? "",
        r.profile?.memberId ?? "", r.profile?.alt1Contact ?? "", r.profile?.alt2Contact ?? "",
        r.profile?.isVerified ? "Yes" : "No",
        r.profile?.age ?? "", r.profile?.aboutYou ?? "", r.profile?.birthOrder ?? "", r.profile?.birthPlace ?? "",
        r.profile?.birthTime ?? "", r.profile?.bodyType ?? "", r.profile?.caste ?? "", r.profile?.economicStatus ?? "",
        r.profile?.familyType ?? "", r.profile?.fatherName ?? "", r.profile?.fatherOccupation ?? "",
        r.profile?.maritalStatus ?? "", r.profile?.motherName ?? "", r.profile?.motherOccupation ?? "",
        r.profile?.motherTongue ?? "", r.profile?.noOfBrothers ?? "", r.profile?.noOfBrothersMarried ?? "",
        r.profile?.noOfChildren ?? "", r.profile?.noOfSisters ?? "", r.profile?.noOfSistersMarried ?? "",
        r.profile?.lagnam ?? "", r.profile?.paadham ?? "", r.profile?.profession ?? "", r.profile?.qualification ?? "",
        r.profile?.raasi ?? "", r.profile?.raghuKedhu ?? "", r.profile?.religion ?? "", r.profile?.salaryDetails ?? "",
        r.profile?.sevvai ?? "", r.profile?.star ?? "", r.profile?.subCaste ?? "", r.profile?.subCasteAdditional ?? "",
        r.profile?.height ?? "", r.profile?.weight ?? "", r.profile?.physicalStatus ?? "",
        r.profile?.nativeCountry ?? "", r.profile?.nativeState ?? "", r.profile?.nativeDistrict ?? "",
        r.profile?.nativeAddress ?? "", r.profile?.country ?? "", r.profile?.district ?? "", r.profile?.state ?? "",
        r.profile?.address ?? "", r.profile?.property ?? "",
        r.created_at ? moment(r.created_at).format("DD/MM/YYYY HH:mm") : "",
        r.updated_at ? moment(r.updated_at).format("DD/MM/YYYY HH:mm") : "",
        r.deleted_at ? moment(r.deleted_at).format("DD/MM/YYYY HH:mm") : "",
        r.createdBy ?? "", r.updatedBy ?? "", r.deletedby ?? "", r.otp ?? ""
      ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(","))
    ];
    const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `all-members-${moment().format("YYYYMMDD-HHmm")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setIsExporting(false);
  };

  const applyFilter = () => {
    filterFieldRef.current = filterField;
    filterValueRef.current = filterValue;
    filterOperatorRef.current = filterOperator;
    filterTypeRef.current = filterType;
    setCurrentPage(1);
    fetchPage(1);
    setFilterModalOpen(false);
  };
  const handleSort = (field: string) => {
    if (sortField === field) setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDirection("asc"); }
  };
  const clearFilter = () => {
    setFilterField(""); setFilterValue(""); setFilterOperator("contains"); setFilterType("");
    filterFieldRef.current = ""; filterValueRef.current = ""; filterOperatorRef.current = "contains"; filterTypeRef.current = "";
    setCurrentPage(1); fetchPage(1); setFilterModalOpen(false);
  };

  return (
    <ScreenLayout padding="medium">
      <div className="app-content">
        <PageHeader
          title="All Members"
          description={
            <>
              Showing {rows.length} of {totalRows} members
              {isLoading && <CircularProgress size={12} sx={{ ml: 1 }} />}
            </>
          }
          actions={
            <>
              <SearchInput value={search} onChange={setSearch} placeholder="Search name, contact, ID…" minWidth={{ xs: "100%", sm: 200, md: 260 }} />
              <Button size="small" variant="contained" startIcon={<FilterListIcon />} onClick={() => !isLoading && setFilterModalOpen(true)} disabled={isLoading}
                sx={{ background: "#212529", color: "#ffffff", "&:hover": { background: "#343a40" }, flex: { xs: 1, sm: "auto" } }}>
                Filter
              </Button>
              <Button size="small" variant="contained" startIcon={isExporting ? <CircularProgress size={14} /> : <DownloadIcon />}
                onClick={exportCSV} disabled={totalRows === 0 || isExporting}
                sx={{ background: "#212529", color: "#ffffff", "&:hover": { background: "#343a40" }, flex: { xs: 1, sm: "auto" } }}>
                {isExporting ? "Exporting…" : "Export CSV"}
              </Button>
            </>
          }
        />

        <Paper sx={{ borderRadius: 2, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)", position: "relative", opacity: isLoading ? 0.6 : 1, transition: "opacity 0.2s", pointerEvents: isLoading ? "none" : "auto" }}>
          <TableContainer sx={{ height: { xs: "calc(100vh - 300px)", sm: "calc(100vh - 220px)" }, overflow: "auto", borderRadius: 2 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  {HEADERS.map((h) => (
                    <TableCell key={h.field} sx={{ ...headSx, cursor: h.field !== "actions" ? "pointer" : "default" }}
                      onClick={() => h.field !== "actions" && handleSort(h.field)}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        {h.label}
                        {sortField === h.field && <Typography sx={{ fontSize: "0.7rem" }}>{sortDirection === "asc" ? "↑" : "↓"}</Typography>}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row, index) => {
                  const t = MEMBER_TYPES.find((m) => m.key === row.memberType);
                  const serialNo = (currentPage - 1) * pageSize + index + 1;
                  return (
                    <TableRow 
                      key={row._uid} 
                      hover 
                      sx={{ 
                        "&:hover": { bgcolor: "#f8fafc" }, 
                        bgcolor: t?.rowBg || "#ffffff",
                        "& td": { bgcolor: "transparent" }
                      }}
                    >
                      <TableCell sx={cellSx}>{serialNo}</TableCell>
                      <TableCell sx={cellSx}>{t && <Chip label={t.label} size="small" sx={{ background: t.bg, color: t.color, fontWeight: 600, fontSize: "0.7rem", height: 22, borderRadius: "12px" }} />}</TableCell>
                      <TableCell sx={cellSx}>{row.id}</TableCell>
                      <TableCell sx={cellSx}>{row.firstName}</TableCell>
                      <TableCell sx={cellSx}>{row.lastName}</TableCell>
                      <TableCell sx={cellSx}>{row.gender}</TableCell>
                      <TableCell sx={cellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <span>{row.username}</span>
                          {row.username && <IconButton icon={<PhoneIcon />} size="xs" buttonColor="#212529" onClick={() => { setCallNumber(row.username); setCallModalOpen(true); }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <span>{row?.profile?.alt1Contact || "—"}</span>
                          {row?.profile?.alt1Contact && <IconButton icon={<PhoneIcon />} size="xs" buttonColor="#212529" onClick={() => { setCallNumber(row?.profile?.alt1Contact || ""); setCallModalOpen(true); }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <span>{row?.profile?.alt2Contact || "—"}</span>
                          {row?.profile?.alt2Contact && <IconButton icon={<PhoneIcon />} size="xs" buttonColor="#212529" onClick={() => { setCallNumber(row?.profile?.alt2Contact || ""); setCallModalOpen(true); }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx}>{row?.profile?.memberId || "N/A"}</TableCell>
                      <TableCell sx={cellSx}>{getStateDisplay(row?.profile?.nativeState)}</TableCell>
                      <TableCell sx={cellSx}>{getStateDisplay(row?.profile?.state)}</TableCell>
                      <TableCell sx={cellSx}>{getHasProfileImage(row) ? "Yes" : "No"}</TableCell>
                      <TableCell sx={cellSx}>{row.dateOfBirth ? moment(row.dateOfBirth).format("DD/MM/YYYY") : "—"}</TableCell>
                      <TableCell sx={cellSx}>{row.created_at ? moment(row.created_at).format("DD/MM/YYYY, h:mm a") : "—"}</TableCell>
                      <TableCell sx={cellSx}>
                        <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                          {row.hasWeb && <Chip label="Web" size="small" sx={{ background: "#e3f2fd", color: "#1976d2", fontSize: "0.65rem", height: 20 }} />}
                          {row.hasAndroidApp && <Chip label="Android" size="small" sx={{ background: "#c8e6c9", color: "#388e3c", fontSize: "0.65rem", height: 20 }} />}
                          {row.hasIosApp && <Chip label="iOS" size="small" sx={{ background: "#fff3e0", color: "#f57c00", fontSize: "0.65rem", height: 20 }} />}
                        </Box>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <Switch size="small" checked={row?.profile?.isVerified ?? false} onChange={(e) => activateUser(row.id, e.target.checked)} />
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <button className="btn btn-sm" title="Hard Delete" onClick={() => hardDeleteUser(row.id)}>
                          <i className="fa fa-user-times" style={{ color: "#b91c1c" }} />
                        </button>
                      </TableCell>
                      <TableCell sx={cellSx}>
                        <button className="btn btn-sm" title="View All" onClick={() => { setSelectedRowData(row); setAllDataModalOpen(true); }}><i className="fa fa-eye" style={{ color: "#212529" }} /></button>
                        <button className="btn btn-sm" title="View" onClick={() => { setSelectedUser(row); setModalShow(true); }}><i className="fa fa-money" style={{ color: "#212529" }} /></button>
                        <button className="btn btn-sm" title="Block" onClick={() => blockUser(row.id)}><i className="fa fa-trash" style={{ color: "#212529" }} /></button>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {isLoading && (
                  <TableRow>
                    <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 2 }}><CircularProgress size={24} /></TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mt: 2, flexWrap: "wrap", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "#64748b" }}>Rows per page:</Typography>
              <CustomSelect value={pageSize} onChange={(val) => { if (isLoading) return; setPageSize(val as number); setCurrentPage(1); fetchPage(1, val as number); }} options={[10, 25, 50, 100]} minWidth={80} />
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="body2" sx={{ color: "#64748b" }}>Go to page:</Typography>
              <CustomSelect value={currentPage} onChange={(val) => { if (isLoading) return; fetchPage(val as number); }} options={Array.from({ length: totalPages }, (_, i) => i + 1)} minWidth={80} />
            </Box>
          </Box>
          <Pagination count={totalPages} page={currentPage} onChange={(_, page) => { if (isLoading) return; fetchPage(page); }} color="primary" size="small" siblingCount={0} disabled={isLoading}
            sx={{ "& .MuiPaginationItem-root": { color: "#212529", "&.Mui-selected": { backgroundColor: "#212529", color: "#ffffff" } } }} />
        </Box>

        {selectedUser && (
          <ViewModal modalShow={modalShow} user={selectedUser} updateModal={(data: { modalShow: boolean }) => { setModalShow(data.modalShow); if (!data.modalShow) setSelectedUser(null); }} />
        )}

        <CustomModal open={filterModalOpen} onClose={() => setFilterModalOpen(false)} title="Filter Members" maxWidth="xs"
          actions={<Box sx={{ display: "flex", gap: 1 }}><CustomButton variant="secondary" onClick={clearFilter}>Clear</CustomButton><CustomButton variant="secondary" onClick={() => setFilterModalOpen(false)}>Cancel</CustomButton><CustomButton variant="primary" onClick={applyFilter}>Apply</CustomButton></Box>}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>Type</Typography>
              <CustomSelect value={filterType} onChange={(val) => { setFilterType(String(val)); if (val) { setFilterField(""); setFilterValue(""); } }}
                options={[{ label: "All Types", value: "" }, { label: "Free", value: "free" }, { label: "Premium", value: "premium" }, { label: "Expired", value: "expired" }, { label: "Deleted", value: "deleted" }]}
                labelKey="label" valueKey="value" minWidth="100%" />
            </Box>
            <Box sx={{ opacity: filterType ? 0.5 : 1, pointerEvents: filterType ? "none" : "auto" }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Field</Typography>
              <CustomSelect value={filterField} onChange={(val) => { setFilterField(String(val)); if (val) { setFilterType(""); } }}
                options={[{ label: "Select Field", value: "" }, { label: "First Name", value: "firstName" }, { label: "Last Name", value: "lastName" }, { label: "All Contacts", value: "allContacts" }, { label: "Email", value: "email" }, { label: "Gender", value: "gender" }, { label: "Status", value: "status" }]}
                labelKey="label" valueKey="value" minWidth="100%" />
            </Box>
            <Box sx={{ opacity: filterType ? 0.5 : 1, pointerEvents: filterType ? "none" : "auto" }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Operator</Typography>
              <CustomSelect value={filterOperator} onChange={(val) => setFilterOperator(String(val))}
                options={[{ label: "Equals", value: "equals" }, { label: "Contains", value: "contains" }, { label: "Starts With", value: "startsWith" }, { label: "Ends With", value: "endsWith" }]}
                labelKey="label" valueKey="value" minWidth="100%" />
            </Box>
            <Box sx={{ opacity: filterType ? 0.5 : 1, pointerEvents: filterType ? "none" : "auto" }}>
              <Typography variant="body2" sx={{ mb: 1 }}>Value</Typography>
              <CustomInput value={filterValue || ""} onChange={(val) => setFilterValue(typeof val === "object" ? (val as React.ChangeEvent<HTMLInputElement>).target.value : String(val))} placeholder="Enter value..." fullWidth />
            </Box>
          </Box>
        </CustomModal>

        <CustomModal open={callModalOpen} onClose={() => setCallModalOpen(false)} title="Call Confirmation" maxWidth="xs"
          actions={<><Button onClick={() => setCallModalOpen(false)}>Cancel</Button><Button variant="contained" onClick={() => { window.location.href = `tel:${callNumber}`; setCallModalOpen(false); }} sx={{ background: "#10b981", "&:hover": { background: "#059669" } }}>Call Now</Button></>}>
          <Typography variant="body1">Do you want to call <strong>{callNumber}</strong>?</Typography>
        </CustomModal>

        <CustomModal open={allDataModalOpen} onClose={() => setAllDataModalOpen(false)} title="All Member Data" maxWidth="md"
          actions={<Button onClick={() => setAllDataModalOpen(false)}>Close</Button>}>
          {selectedRowData && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#212529", mb: 1.5, pb: 1, borderBottom: "1px solid #e5e7eb" }}>Personal Information</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
                  <DataField label="ID" value={selectedRowData.id} />
                  <DataField label="Name" value={`${selectedRowData.firstName || ""} ${selectedRowData.lastName || ""}`} />
                  <DataField label="Gender" value={selectedRowData.gender} />
                  <DataField label="Date of Birth" value={selectedRowData.dateOfBirth ? moment(selectedRowData.dateOfBirth).format("DD/MM/YYYY") : "—"} />
                  <DataField label="Email" value={selectedRowData.email} />
                  <DataField label="Mobile" value={`${selectedRowData.countryCode || ""} ${selectedRowData.mobile || ""}`} />
                </Box>
              </Box>
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#212529", mb: 1.5, pb: 1, borderBottom: "1px solid #e5e7eb" }}>Account Status</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
                  <DataField label="Status" value={selectedRowData.status} />
                  <DataField label="Active" value={selectedRowData.isActive ? "Yes" : "No"} />
                  <DataField label="Email Verified" value={selectedRowData.isEmailVerified ? "Yes" : "No"} />
                  <DataField label="Mobile Verified" value={selectedRowData.isMobileVerified ? "Yes" : "No"} />
                  <DataField label="Member Type" value={selectedRowData.memberType} />
                  <DataField label="Roles" value={selectedRowData.roles?.map((r: { name: string }) => r.name).join(", ") || "—"} />
                </Box>
              </Box>
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#212529", mb: 1.5, pb: 1, borderBottom: "1px solid #e5e7eb" }}>Timestamps</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
                  <DataField label="Created At" value={selectedRowData.created_at ? moment(selectedRowData.created_at).format("DD/MM/YYYY, HH:mm") : "—"} />
                  <DataField label="Updated At" value={selectedRowData.updated_at ? moment(selectedRowData.updated_at).format("DD/MM/YYYY, HH:mm") : "—"} />
                  <DataField label="Deleted At" value={selectedRowData.deleted_at ? moment(selectedRowData.deleted_at).format("DD/MM/YYYY, HH:mm") : "—"} />
                </Box>
              </Box>
              <Box sx={{ border: "1px solid #e5e7eb", borderRadius: 2, p: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#212529", mb: 1.5, pb: 1, borderBottom: "1px solid #e5e7eb" }}>Additional Information</Typography>
                <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 1.5 }}>
                  <DataField label="On Behalf" value={selectedRowData.onBehalf?.name || "—"} />
                  <DataField label="Android App" value={selectedRowData.hasAndroidApp ? "Yes" : "No"} />
                  <DataField label="iOS App" value={selectedRowData.hasIosApp ? "Yes" : "No"} />
                  <DataField label="Web" value={selectedRowData.hasWeb ? "Yes" : "No"} />
                </Box>
              </Box>
            </Box>
          )}
        </CustomModal>

        <CustomModal open={confirmModalOpen} onClose={() => { setConfirmModalOpen(false); setConfirmAction(null); }} title="Confirm Action" maxWidth="sm"
          actions={<><CustomButton variant="secondary" onClick={() => { setConfirmModalOpen(false); setConfirmAction(null); }}>Cancel</CustomButton><CustomButton variant="primary" onClick={handleConfirmAction}>Confirm</CustomButton></>}>
          <Typography variant="body1">
            {confirmAction?.type === "activate"
              ? `Are you sure you want to ${confirmAction.value ? "activate" : "deactivate"} this user?`
              : confirmAction?.type === "hardDelete"
                ? "Are you sure you want to hard delete this user permanently? This cannot be undone."
                : "Are you sure you want to block/delete this user?"}
          </Typography>
        </CustomModal>
      </div>
    </ScreenLayout>
  );
}
